import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Props {
  errors?: Record<string, string>
}

export default function NewOrganization({ errors = {} }: Props) {
  const { t } = useTranslation()
  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("organizations.title", "Organizations"),
      href: "/organizations",
    },
    { title: t("organizations.new.title", "New"), href: "/organizations/new" },
  ]
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const getInitial = (n: string) => {
    const trimmed = n.trim()
    return trimmed ? trimmed.charAt(0).toUpperCase() : "O"
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoPreview(URL.createObjectURL(file))
    }
  }

  const handleNameChange = (value: string) => {
    setName(value)
    // Auto-generate slug from name if slug hasn't been manually edited
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value))
    }
  }

  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    setProcessing(true)

    const formData = new FormData()
    formData.append("organization[name]", name)
    formData.append("organization[slug]", slug)
    if (fileRef.current?.files?.[0]) {
      formData.append("organization[logo]", fileRef.current.files[0])
    }

    router.post("/organizations", formData, {
      forceFormData: true,
      onFinish: () => setProcessing(false),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("organizations.new.title", "Create Organization")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div>
          <Link
            href="/organizations"
            className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="size-4" />
            {t("organizations.new.back", "Back to Organizations")}
          </Link>
          <HeadingSmall
            title={t("organizations.new.heading", "Create a New Organization")}
            description={t(
              "organizations.new.description",
              "Give your organization a name. You can invite team members later.",
            )}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo */}
          <div className="space-y-2">
            <Label>{t("organization_settings.logo", "Logo")}</Label>
            <div className="flex items-center gap-4">
              <div className="border-border bg-primary/10 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2">
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-primary text-2xl font-bold">
                    {getInitial(name)}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                >
                  {t("organizations.new.upload_logo", "Upload Logo")}
                </Button>
                <p className="text-muted-foreground text-xs">
                  {t("organization_settings.logo_hint", "PNG, JPG up to 2MB")}
                </p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLogoChange}
              />
            </div>
            {errors.logo && <InputError message={errors.logo} />}
          </div>

          {/* Name */}
          <div className="grid gap-2">
            <Label htmlFor="org-name">
              {t("organization_settings.name", "Name")}
            </Label>
            <Input
              id="org-name"
              placeholder={t(
                "organizations.new.name_placeholder",
                "e.g. The Origin Roastery",
              )}
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              autoFocus
              required
              className="max-w-sm"
            />
            {errors.name && <InputError message={errors.name} />}
          </div>

          {/* Slug */}
          <div className="grid gap-2">
            <Label htmlFor="org-slug">
              {t("organization_settings.slug", "Slug")}
            </Label>
            <Input
              id="org-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              required
              pattern="[a-z0-9-]+"
              className="max-w-sm font-mono text-sm"
            />
            <p className="text-muted-foreground text-xs">
              {t(
                "organization_settings.slug_hint",
                "Only lowercase letters, numbers, and hyphens.",
              )}
            </p>
            {errors.slug && <InputError message={errors.slug} />}
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={!name.trim() || processing}>
              {processing
                ? t("organizations.new.creating", "Creating...")
                : t("organizations.new.create_button", "Create Organization")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => router.visit("/organizations")}
              disabled={processing}
            >
              {t("organizations.new.cancel", "Cancel")}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
